import type { LoginResult } from '@/types/auth'
import { isUnknownRecord } from '@/types/json'
import type {
  ApplicationEntry,
  AuthorizationContext,
  AuthorizationJoinApplication,
  AuthorizationRole,
  AuthorizationUser,
  AuthorizationWorkspace,
  DataScope,
  EffectivePermissionMap,
  EffectivePermissionScope,
  SensitiveFieldPermission,
  TenantType,
  WorkspaceAccessState,
  WorkspaceStatus,
} from '@/types/workspace'

const DATA_SCOPES = [
  'public',
  'self',
  'assigned',
  'classroom',
  'academic_grade',
  'organization',
  'Workspace',
  'platform',
] as const satisfies readonly DataScope[]

const APPLICATIONS = ['frontend', 'admin'] as const satisfies readonly ApplicationEntry[]

const WORKSPACE_ACCESS_STATES = [
  'active',
  'pending_approval',
  'selection_required',
  'frozen',
  'membership_suspended',
] as const satisfies readonly WorkspaceAccessState[]

const FIELD_PERMISSIONS = [
  'field.contact.view',
  'field.identity.view',
  'field.learning_detail.view',
  'field.payment.view',
  'field.settlement_account.view',
] as const satisfies readonly SensitiveFieldPermission[]

const TENANT_TYPES = [
  'company',
  'school',
  'institution',
  'franchisee',
  'personal',
  'study_room',
  'childcare',
] as const satisfies readonly TenantType[]

const JOIN_APPLICATION_STATUSES = [
  'pending',
] as const satisfies readonly AuthorizationJoinApplication['status'][]

const isStringLiteral = <T extends string>(value: unknown, candidates: readonly T[]): value is T =>
  typeof value === 'string' && candidates.some(candidate => candidate === value)

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback

const asNullableNumber = (value: unknown): number | null => {
  const normalized = asNumber(value)
  return normalized > 0 ? normalized : null
}

const normalizeStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : []

const normalizeScopes = (value: unknown): DataScope[] => {
  if (!Array.isArray(value)) return []
  return value.filter((scope): scope is DataScope => isStringLiteral(scope, DATA_SCOPES))
}

const normalizePermissionScope = (value: unknown): EffectivePermissionScope => {
  if (!isUnknownRecord(value)) {
    return { read_scopes: [], write_scopes: [] }
  }

  return {
    read_scopes: normalizeScopes(value['read_scopes']),
    write_scopes: normalizeScopes(value['write_scopes']).filter(scope => scope !== 'public'),
  }
}

/** 将后端权限快照规范化为「权限码 -> 独立读写范围」。 */
const normalizePermissions = (value: unknown): EffectivePermissionMap => {
  if (!isUnknownRecord(value)) return {}

  return Object.entries(value).reduce<EffectivePermissionMap>((permissions, [code, scope]) => {
    permissions[code] = normalizePermissionScope(scope)
    return permissions
  }, {})
}

const normalizeUser = (value: unknown): AuthorizationUser => {
  const user = isUnknownRecord(value) ? value : {}
  return {
    id: asNumber(user['id']),
    phone: asString(user['phone']),
    nickname:
      typeof user['nickname'] === 'string' || user['nickname'] === null ? user['nickname'] : null,
    avatar: typeof user['avatar'] === 'string' || user['avatar'] === null ? user['avatar'] : null,
  }
}

const normalizeRoles = (value: unknown): AuthorizationRole[] => {
  if (!Array.isArray(value)) return []
  return value.flatMap(item => {
    if (!isUnknownRecord(item)) return []
    return [
      {
        id: asNumber(item['id']),
        code: asString(item['code']),
        name: asString(item['name']),
      },
    ]
  })
}

const normalizeApplications = (value: unknown): ApplicationEntry[] =>
  Array.isArray(value)
    ? value.filter((item): item is ApplicationEntry => isStringLiteral(item, APPLICATIONS))
    : []

/** 规范化认证上下文中的待审核申请摘要。 */
const normalizeJoinApplication = (value: unknown): AuthorizationJoinApplication | null => {
  if (!isUnknownRecord(value)) return null

  const status = value['status']
  if (!isStringLiteral(status, JOIN_APPLICATION_STATUSES)) return null

  const workspace = isUnknownRecord(value['workspace']) ? value['workspace'] : null
  const workspaceName = asString(value['workspace_name'] || workspace?.['name'])
  const id = asNullableNumber(value['id'])
  if (!id || !workspaceName) return null

  return {
    id,
    status,
    workspace_name: workspaceName,
  }
}

/**
 * 规范化认证上下文，输出单 Workspace 权限模型。
 */
export const normalizeAuthorizationContext = (value: unknown): AuthorizationContext | null => {
  if (!isUnknownRecord(value)) return null
  const rawWorkspace = isUnknownRecord(value['workspace']) ? value['workspace'] : null

  const permissions = normalizePermissions(value['effective_permissions'] ?? value['permissions'])
  const rawFieldPermissions = Array.isArray(value['field_permissions'])
    ? value['field_permissions']
    : []
  const fieldPermissions = rawFieldPermissions.filter(
    (permission): permission is SensitiveFieldPermission =>
      isStringLiteral(permission, FIELD_PERMISSIONS)
  )

  const accessState = isStringLiteral(value['access_state'], WORKSPACE_ACCESS_STATES)
    ? value['access_state']
    : 'active'
  const tenantId = rawWorkspace ? asNumber(rawWorkspace['tenant_id'] ?? value['tenant_id']) : 0
  let workspace: AuthorizationWorkspace | null = null
  if (rawWorkspace && tenantId > 0) {
    const tenantType = isStringLiteral(rawWorkspace['tenant_type'], TENANT_TYPES)
      ? rawWorkspace['tenant_type']
      : 'institution'
    const status: WorkspaceStatus = rawWorkspace['status'] === 'disabled' ? 'disabled' : 'active'
    workspace = {
      id: asNumber(rawWorkspace['id']),
      name: asString(rawWorkspace['name']),
      code: asString(rawWorkspace['code']),
      tenant_id: tenantId,
      tenant_name: asString(rawWorkspace['tenant_name']),
      tenant_type: tenantType,
      is_company: rawWorkspace['is_company'] === true,
      status,
    }
  }

  if (accessState === 'active' && !workspace) return null

  return {
    user: normalizeUser(value['user']),
    must_change_password: value['must_change_password'] === true,
    access_state: accessState,
    blocking_reasons: normalizeStringList(value['blocking_reasons']),
    recovery_actions: normalizeStringList(value['recovery_actions']),
    member_id: asNullableNumber(value['member_id'] ?? rawWorkspace?.['member_id']),
    workspace,
    join_application: normalizeJoinApplication(value['join_application']),
    roles: normalizeRoles(value['roles']),
    permissions,
    field_permissions: fieldPermissions,
    applications: normalizeApplications(value['applications']),
  }
}

/** 从统一登录响应中提取并规范化权限上下文。 */
export const getLoginAuthorizationContext = (payload: LoginResult): AuthorizationContext | null => {
  return normalizeAuthorizationContext(payload.auth_context)
}

/** Frontend 只接受具备学习入口的账号。 */
export const canUseFrontendAuthorization = (context: AuthorizationContext | null): boolean =>
  context !== null && context.access_state === 'active' && context.applications.includes('frontend')
